// ConfirmSheet — the universal high-impact action pattern (Blueprint §4.4.2 / D-6).
//
// Every mutating operator action routes through this: a focused sheet with a
// required reason, an optional before→after preview, an optional step-up notice,
// and explicit confirm/cancel. It does not perform the mutation itself — the
// caller passes onConfirm(reason) and handles the callable + audit toast.

import { useState } from "react";
import type { ReactNode } from "react";

export function ConfirmSheet({
  open,
  title,
  description,
  before,
  after,
  reasonRequired = true,
  requiresStepUp = false,
  confirmLabel = "Confirm",
  destructive = false,
  busy = false,
  error,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  description?: string;
  before?: string;
  after?: string;
  reasonRequired?: boolean;
  requiresStepUp?: boolean;
  confirmLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  error?: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState("");
  if (!open) return null;
  const canConfirm = (!reasonRequired || reason.trim().length > 0) && !busy;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
        <h3 className="text-base font-black text-slate-900">{title}</h3>
        {description ? <p className="mt-1 text-sm text-slate-500">{description}</p> : null}

        {before !== undefined || after !== undefined ? (
          <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <span className="font-mono">{before ?? "—"}</span> <span className="text-slate-400">→</span>{" "}
            <span className="font-mono font-bold">{after ?? "—"}</span>
          </p>
        ) : null}

        {children}

        {reasonRequired ? (
          <label className="mt-3 flex flex-col gap-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            Reason
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required — recorded in the audit trail"
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-normal normal-case text-slate-900"
            />
          </label>
        ) : null}

        {requiresStepUp ? <p className="mt-2 text-[11px] font-semibold text-amber-600">⚠ Requires re-authentication (step-up).</p> : null}
        {error ? <p className="mt-2 text-sm font-semibold text-red-600">{error}</p> : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 disabled:opacity-50">Cancel</button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={!canConfirm}
            className={`rounded-xl px-4 py-2 text-sm font-black text-white disabled:opacity-50 ${destructive ? "bg-red-600" : "bg-slate-900"}`}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
